<template>
  <div class="patient-header">
    <div class="header-left">
      <div class="patient-avatar">
        <img v-if="avatarSrc" :src="avatarSrc" :alt="patientName" class="avatar-image" />
        <div v-else class="avatar-placeholder">
          {{ initials }}
        </div>
      </div>

      <div class="patient-content">
        <div class="patient-row patient-row--primary">
          <div class="patient-name-group">
            <!-- <span class="patient-name" :class="{ 'is-new': isNameNew }">{{ patientName }}</span> -->
            <NameReveal
              class="patient-name"
              :class="{ 'is-new': isNameNew }"
              :name="patientData.nameDisplay"
            />
            <span v-if="patientGender" class="patient-gender" :class="{ 'is-new': isGenderNew }">
              {{ patientGender }}
            </span>
            <span v-if="patientAgeText" class="patient-age" :class="{ 'is-new': isAgeNew }">
              {{ patientAgeText }}
            </span>
            <span
              class="patient-age"
              v-if="maritalStatusText"
              :class="{ 'is-new': isMaritalStatusNew }"
            >
              {{ maritalStatusText }}
            </span>
          </div>
        </div>

        <div class="patient-row patient-row--secondary">
          <div class="patient-id">患者ID: {{ patientId }}</div>
        </div>

        <div v-if="displayTags.length" class="patient-row patient-row--tags">
          <div class="patient-tags">
            <span
              v-for="(tag, index) in displayTags"
              :key="index"
              class="patient-tag"
              :class="{ 'is-new': tag.isNew }"
            >
              {{ tag.text }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="header-middle">
      <div class="detail-item" :class="{ 'is-new': isInitialVisitDateNew }">
        <span class="detail-label">初诊时间:</span>
        <span class="detail-value">{{ initialVisitDate }}</span>
      </div>
      <div class="detail-item" :class="{ 'is-new': isPhoneNew }">
        <span class="detail-label">手机号:</span>
        <span class="detail-value">{{ patientPhone }}</span>
      </div>
      <div class="detail-item" :class="{ 'is-new': isCommunicationCountNew }">
        <span class="detail-label">沟通次数:</span>
        <span class="detail-value">{{ treatmentFrequencyText }}</span>
      </div>
    </div>

    <div v-if="completenessPercent < 100" class="header-right">
      <div class="completeness-box" :class="{ 'is-new': isCompletenessNew }">
        <div class="completeness-progress">
          <a-progress
            type="circle"
            :percent="completenessPercent"
            :width="72"
            :format="percent => `${percent}%`"
            :stroke-color="{
              '0%': '#97C6FF',
              '100%': '#1890FF'
            }"
          />
        </div>
        <div class="completeness-label">信息完善度</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue'
import { createHeaderDisplayTags } from '../model/headerTags'
import { createIsNewFieldChecker } from '../model/portraitView'
import NameReveal from './NameReveal.vue'

const props = defineProps({
  patientData: {
    type: Object,
    default: () => ({})
  }
})

const patientName = computed(() => props.patientData?.nameDisplay || '未知患者')
const patientGender = computed(() => {
  const gender = props.patientData?.genderLabel || props.patientData?.gender
  if (gender === 1 || gender === '1' || gender === '男') return '男'
  if (gender === 0 || gender === '0' || gender === '女') return '女'
  return gender || ''
})
const patientAge = computed(() => props.patientData?.age)
const patientAgeText = computed(() => (isFilled(patientAge.value) ? `${patientAge.value}岁` : ''))
const maritalStatusText = computed(() => {
  const maritalStatus = props.patientData?.maritalStatusLabel
  return isFilled(maritalStatus) ? maritalStatus : ''
})
const patientId = computed(() => props.patientData?.ucPatientId || '--')
const patientPhone = computed(() => props.patientData?.phone || '--')
const initialVisitDate = computed(() => props.patientData?.firstVisitDate || '--')
const treatmentFrequency = computed(() => props.patientData?.communicationCount || '--')
const treatmentFrequencyText = computed(() => {
  const value = treatmentFrequency.value
  if (!isFilled(value)) return '--'
  if (typeof value === 'number') return `${value}次`
  return /次$/.test(String(value)) ? String(value) : `${value}次`
})
const avatarSrc = computed(() => props.patientData?.headUrl || '')

const displayTags = computed(() => createHeaderDisplayTags(props.patientData, isNewField.value))

// 字段更新标记（红点）：仅覆盖页面直接展示的字段
const isNewField = computed(() => createIsNewFieldChecker(props.patientData?.newFieldCodes))
const isNameNew = computed(() => isNewField.value('nameDisplay'))
const isGenderNew = computed(() => isNewField.value('gender', 'genderLabel'))
const isAgeNew = computed(() => isNewField.value('age'))
const isMaritalStatusNew = computed(() => isNewField.value('maritalStatus', 'maritalStatusLabel'))
const isInitialVisitDateNew = computed(() => isNewField.value('firstVisitDate'))
const isPhoneNew = computed(() => isNewField.value('phone'))
const isCommunicationCountNew = computed(() => isNewField.value('communicationCount'))
const isCompletenessNew = computed(() => isNewField.value('completionPercent'))

const initials = computed(() => {
  const name = patientName.value
  return name ? name.substring(0, 1) : '患'
})

const isFilled = value => {
  if (Array.isArray(value)) return value.length > 0
  return value !== undefined && value !== null && value !== '' && value !== '--'
}

// 计算完善度：根据基本信息的填充情况
const completenessPercent = computed(() => {
  const directPercent = props.patientData?.completionPercent
  if (directPercent !== undefined && directPercent !== null && directPercent !== '') {
    const parsedPercent = Number(directPercent)
    if (!Number.isNaN(parsedPercent)) {
      return Math.max(0, Math.min(100, Math.round(parsedPercent)))
    }
  }

  const patientData = props.patientData || {}

  let filled = 0
  let total = 0

  // 基本信息字段
  const basicFields = [
    'nameDisplay',
    'gender',
    'age',
    'phone',
    'idCard',
    'occupation',
    'maritalStatus',
    'currentStatusMedicationAdherenceValue'
  ]
  basicFields.forEach(field => {
    total++
    if (isFilled(patientData[field])) {
      filled++
    }
  })

  // 摘要字段
  const summaryFields = ['followUpCountValue', 'currentStatus', 'keyRiskLabel', 'todoItems']
  summaryFields.forEach(field => {
    total++
    if (isFilled(patientData[field])) {
      filled++
    }
  })

  // 画像字段
  const portraitFields = [
    'currentDiseasePhaseValue',
    'currentEngagementStageValue',
    'currentMedication',
    'emotionStateValue',
    'responsePatternValue',
    'contactPreferenceValue',
    'communicationComplianceValue'
  ]
  portraitFields.forEach(field => {
    total++
    if (isFilled(patientData[field])) {
      filled++
    }
  })

  return Math.round((filled / total) * 100) || 0
})
</script>
<style lang="scss" scoped>
.patient-header {
  display: flex;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #eef1f5;
  border-radius: 4px;
  padding: 20px;
  margin-top: 12px;
  box-shadow: 0 1px 2px rgba(15, 34, 58, 0.04);

  .header-left,
  .header-middle,
  .header-right {
    display: flex;
    min-width: 0;
  }

  .header-left {
    gap: 10px;
    padding-right: 14px;
    margin-right: 21px;
    border-right: 1px solid #edf0f4;
    flex-shrink: 0;
  }

  .header-middle {
    gap: 32px 92px;
    flex: 1;
    padding-right: 24px;
    margin-right: 24px;
    height: 100%;
    flex-wrap: wrap;
  }

  .header-right {
    flex-shrink: 0;
  }

  .patient-avatar {
    flex-shrink: 0;
    width: 60px;
    height: 60px;
    border-radius: 6px;
    overflow: hidden;

    .avatar-image,
    .avatar-placeholder {
      width: 100%;
      height: 100%;
    }

    .avatar-image {
      display: block;
      object-fit: cover;
    }

    .avatar-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(135deg, #ffb36b 0%, #f36b61 100%);
      color: #fff;
      font-size: 20px;
      font-weight: 600;
    }
  }

  .patient-content {
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  .patient-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 18px;
    min-width: 0;
  }

  .patient-row--primary {
    gap: 10px 12px;
  }

  .patient-row--secondary,
  .patient-row--tags {
    gap: 6px 12px;
  }

  .patient-row--tags {
    align-items: flex-start;
  }

  .patient-name-group {
    display: flex;
    align-items: baseline;
    gap: 8px;
    flex-shrink: 0;

    .patient-name {
      font-size: 16px;
      line-height: 28px;
      font-weight: 600;
      color: #333;
    }

    .patient-gender,
    .patient-age {
      font-size: 16px;
      line-height: 28px;
      font-weight: 600;
      color: #333;
    }
  }

  .patient-id {
    font-size: 12px;
    line-height: 18px;
    color: #7d8592;
    margin-top: -5px;
  }

  .patient-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .patient-tag {
      display: inline-flex;
      align-items: center;
      padding: 2px 6px;
      border-radius: 4px;
      background: #e4edff;
      color: #4676d6;
      font-size: 12px;
      font-weight: 500;
    }
  }

  .detail-item {
    font-size: 13px;
    line-height: 20px;
    white-space: nowrap;

    .detail-label {
      color: #7d8592;
      margin-right: 4px;
    }

    .detail-value {
      color: #2f3136;
      font-weight: 500;
    }
  }
  .detail-item:last-child {
    width: 100%;
  }

  .completeness-box {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    padding-left: 8px;

    .completeness-progress {
      width: 72px;
      height: 72px;
    }

    .completeness-label {
      font-size: 14px;
      line-height: 20px;
      color: #5d81a8;
      white-space: nowrap;
    }
  }

  :deep(.ant-progress-circle .ant-progress-text) {
    font-size: 16px;
    font-weight: 600;
    color: #3d8bff;
  }
}

@media (max-width: 1280px) {
  .patient-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 16px;

    .header-left,
    .header-middle,
    .header-right,
    .patient-row {
      width: 100%;
    }

    .header-left,
    .header-middle {
      padding-right: 0;
      margin-right: 0;
      border-right: none;
      padding-bottom: 12px;
      border-bottom: 1px solid #edf0f4;
    }

    .header-middle {
      flex-wrap: wrap;
      gap: 12px 28px;
    }
  }
}

@media (max-width: 768px) {
  .patient-header {
    padding: 12px;

    .patient-name-group .patient-name {
      font-size: 18px;
    }

    .completeness-box {
      padding-left: 0;
    }
  }
}

.patient-name,
.patient-gender,
.patient-age,
.patient-tag,
.detail-value,
.completeness-label {
  position: relative;
}

.patient-name.is-new::after,
.patient-gender.is-new::after,
.patient-age.is-new::after,
.patient-tag.is-new::after,
.detail-item.is-new .detail-value::after,
.completeness-box.is-new .completeness-label::after {
  content: '';
  position: absolute;
  top: 0;
  left: 100%;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #ff2b2b;
}
</style>
